import { Box, ThemeProvider, Typography, useTheme } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { RepresentativeCompanyAndTruckModal } from 'app/modules/company/RepresentativeCompanyAndTruckModal';
import RepresentativeCompanyModal from 'app/modules/company/RepresentativeCompanyModal';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { ActivatedSwitch } from 'app/shared/components/activated-switch/ActivatedSwitch';
import { ImageInput } from 'app/shared/components/image-input/ImageInput';
import { BoxForm, FormControlDefault, MenuItemStyled, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import { CompanyType } from 'app/shared/model/enumerations/company-type.model';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { AlertDefaultSelectionModal } from '../../modules/company/companyAlertDefaultSelectionModal';
import { getRepresentativeCompaniesByCompany } from '../representative-company/representative-company.reducer';
import { getRepresentativeTruckImplementsByCompany } from '../representative-truck-implement/representative-truck-implement.reducer';
import { createEntity, getEntity, reset, updateEntity } from './company.reducer';

export const CompanyUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const entitiesRepresentativeTruckImplements = useAppSelector(state => state.representativeTruckImplement.entitiesAlt);
  const entitiesRepresentativeCompanies = useAppSelector(state => state.representativeCompany.entities);

  const updateSuccess = useAppSelector(state => state.company.updateSuccess);
  const companyEntity = useAppSelector(state => state.company.entity);
  const loading = useAppSelector(state => state.company.loading);

  const [representativeTruckImplements, setRepresentativeTruckImplements] = useState<number[]>([]);
  const [representativeCompanies, setRepresentativeCompanies] = useState<number[]>();
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [openModal, setOpenModal] = useState(false);
  const [checked, setChecked] = useState(true);
  const [formData, setFormData] = useState({
    id: null,
    image: '',
    imageContentType: '',
    name: '',
    identification: '',
    type: null,
    activated: true,
  });

  const companyType: CompanyType[] = [CompanyType.TRANSPORTER, CompanyType.REPRESENTATIVE, CompanyType.MANUFACTURER];

  const isAllAdmins = isAdmin || isManufacturerAdmin || isRepresentativeAdmin;

  const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
  const [openAlertModal, setOpenAlertModal] = useState(false);

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
      dispatch(getRepresentativeCompaniesByCompany(props.match.params.id));
      dispatch(getRepresentativeTruckImplementsByCompany(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    if (!isNew && companyEntity) {
      setFormData({
        id: companyEntity?.id,
        image: companyEntity?.logo,
        imageContentType: companyEntity?.logoContentType,
        name: companyEntity?.name,
        identification: companyEntity?.identification,
        activated: companyEntity?.activated,
        type: companyEntity?.type,
      });
    }
  }, [companyEntity]);

  useEffect(() => {
    setChecked(formData['activated']);
  }, [formData]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (entitiesRepresentativeCompanies) {
      setRepresentativeCompanies(entitiesRepresentativeCompanies as number[]);
    }
    if (entitiesRepresentativeTruckImplements) {
      setRepresentativeTruckImplements(entitiesRepresentativeTruckImplements as number[]);
    }
  }, [entitiesRepresentativeCompanies, entitiesRepresentativeTruckImplements]);

  const saveEntity = values => {
    const company = {
      ...companyEntity,
      ...values,
    };
    company.logo = values.image;

    if (!formData['type']) {
      if (isRepresentativeAdmin) {
        company.type = CompanyType.TRANSPORTER;
      }
      if (isManufacturerAdmin) {
        company.type = CompanyType.REPRESENTATIVE;
      }
    }

    const companyRequest = {
      company,
      representativeCompanies,
      representativeTruckImplements,
      defaultAlertIds: selectedAlerts.map(id => id),
    };

    if (isNew) {
      dispatch(createEntity(companyRequest));
    } else {
      dispatch(updateEntity(companyRequest));
    }
  };

  const handleClose = () => {
    props.history.push('/company' + props.location.search);
  };

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const handleSubmit = () => {
    event.preventDefault();

    const trimmedFormData = {
      ...formData,
      name: formData.name.trim(),
    };

    saveEntity(trimmedFormData);
  };

  const handleSave = data => {
    setOpenModal(false);

    setRepresentativeCompanies(data.representativeCompanies);
    setRepresentativeTruckImplements(data.representativeTrucks);
  };

  useEffect(() => {
    if (formData['type'] !== companyEntity?.type) {
      setRepresentativeCompanies([]);
      setRepresentativeTruckImplements([]);
    } else {
      setRepresentativeCompanies(entitiesRepresentativeCompanies as number[]);
      setRepresentativeTruckImplements(entitiesRepresentativeTruckImplements as number[]);
    }
  }, [formData['type']]);

  const getRepresentativeFieldText = (_formData, _representativeCompanies, _representativeTruckImplements) => {
    const companyCount = _representativeCompanies?.length || 0;
    const implementCount = _representativeTruckImplements?.length || 0;

    if (_formData['type'] === CompanyType.MANUFACTURER) {
      return companyCount === 0
        ? 'Nenhum representante selecionado'
        : `${companyCount} representante${companyCount > 1 ? 's' : ''} selecionado${companyCount > 1 ? 's' : ''}`;
    } else {
      if (implementCount > 0) {
        return `${companyCount} empresa${companyCount > 1 ? 's' : ''} selecionada${companyCount > 1 ? 's' : ''} (${implementCount} ativo${
          implementCount > 1 ? 's' : ''
        })`;
      } else {
        if (_formData['type']) {
          return 'Nenhum ativo selecionado';
        } else {
          return '';
        }
      }
    }
  };

  const filterCompany = (_companyType: CompanyType[]) => {
    if (isAdmin) {
      return _companyType;
    }
    if (isManufacturerAdmin) {
      return _companyType.filter(type => type === CompanyType.REPRESENTATIVE);
    }
    return _companyType.filter(type => type === CompanyType.TRANSPORTER);
  };

  const handleSaveAlerts = (alerts: number[]) => {
    setSelectedAlerts(alerts);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <RegisterLayout>
          <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }} justifyContent={'center'}>
            <RegisterTitle isNew={isNew} contentKey={isManufacturerAdmin ? 'fleetSenseApp.company.representative' : 'fleetSenseApp.company'} />
            <Box>
              {loading ? (
                <Box>
                  <Typography>Loading...</Typography>
                </Box>
              ) : (
                <Box margin={'0 auto'} width={'100%'}>
                  <BoxForm onSubmit={handleSubmit}>
                    {!isNew && (
                      <FormControlDefault sx={{ display: 'none' }}>
                        <TypographyForms>{translate('global.field.id')}</TypographyForms>
                        <TextFieldForms value={formData['id']} id={'company-id'} name={'id'} />
                      </FormControlDefault>
                    )}
                    <ImageInput image={formData['image']} imageContentType={formData['imageContentType']} handleInputChange={handleInputChange}></ImageInput>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.company.name')}</TypographyForms>
                      <TextFieldForms
                        value={formData['name']}
                        id={'company-name'}
                        data-cy={'name'}
                        name={'name'}
                        required={true}
                        onChange={e => handleInputChange('name', e.target.value)}
                      />
                    </FormControlDefault>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.company.identification')}</TypographyForms>
                      <TextFieldForms
                        value={formData['identification']}
                        id={'company-identification'}
                        data-cy={'identification'}
                        name={'identification'}
                        required={true}
                        onChange={e => handleInputChange('identification', e.target.value)}
                      />
                    </FormControlDefault>

                    {isAdmin && (
                      <FormControlDefault>
                        <TypographyForms>{translate('fleetSenseApp.company.type')}</TypographyForms>
                        <TextFieldForms
                          value={formData['type'] || ''}
                          onChange={event => handleInputChange('type', event.target.value)}
                          select
                          name={'type'}
                          id={'company-type'}
                          data-cy={'type'}
                          required={true}
                        >
                          {companyType &&
                            filterCompany(companyType).map(type => (
                              <MenuItemStyled value={type} key={type}>
                                {translate(`fleetSenseApp.CompanyType.${type}`)}
                              </MenuItemStyled>
                            ))}
                        </TextFieldForms>
                      </FormControlDefault>
                    )}
                    {isAdmin && (
                      <FormControlDefault>
                        <TypographyForms>Alertas</TypographyForms>
                        <TextFieldForms
                          onClick={() => setOpenAlertModal(true)}
                          value={selectedAlerts.length > 0 ? `${selectedAlerts.length} alerta(s) selecionado(s)` : 'Clique para selecionar alertas'}
                          InputProps={{ readOnly: true }}
                        />
                      </FormControlDefault>
                    )}
                    {formData['type'] && isAdmin && formData['type'] !== CompanyType.TRANSPORTER && (
                      <FormControlDefault>
                        <TypographyForms>{formData['type'] === CompanyType.MANUFACTURER ? 'Representantes' : 'Empresas e Ativos'}</TypographyForms>
                        <TextFieldForms
                          onClick={() => setOpenModal(!openModal)}
                          value={getRepresentativeFieldText(formData, representativeCompanies, representativeTruckImplements)}
                        />
                      </FormControlDefault>
                    )}
                    {!isNew && isAllAdmins && <ActivatedSwitch checked={checked} setChecked={setChecked} handleInputChange={handleInputChange} />}
                    <RegisterButton />
                  </BoxForm>
                </Box>
              )}
            </Box>
          </Box>
        </RegisterLayout>
        {openModal && formData['type'] === CompanyType.REPRESENTATIVE && (
          <RepresentativeCompanyAndTruckModal
            toggleModal={() => setOpenModal(!openModal)}
            handleSave={handleSave}
            representativeCompanies={representativeCompanies}
            representativeTruckImplements={representativeTruckImplements}
            selectedType={formData['type']}
          />
        )}
        {openModal && formData['type'] === CompanyType.MANUFACTURER && (
          <RepresentativeCompanyModal
            toggleModal={() => setOpenModal(!openModal)}
            handleSave={handleSave}
            representativeCompanies={representativeCompanies}
            companyEntity={companyEntity}
          />
        )}
        <AlertDefaultSelectionModal
          open={openAlertModal}
          toggleModal={() => setOpenAlertModal(!openAlertModal)}
          initialSelectedAlerts={[]}
          handleSave={data => {
            handleSaveAlerts(data.selectedAlerts);
          }}
          companyId={props.match.params?.id != null ? props.match.params?.id : ''}
        />
      </ThemeProvider>
    </>
  );
};
